import React from 'react';
import { Church, Heart, Book, Calendar } from 'lucide-react';

const Faith = () => {
  const devotionals = [
    {
      title: 'Fé que Move Montanhas',
      verse: '"Se vocês tiverem fé do tamanho de um grão de mostarda... nada lhes será impossível." - Mateus 17:20',
      message: 'A fé não é medida pelo seu tamanho, mas pela sua sinceridade e confiança em Deus. Mesmo pequena, quando genuina, pode realizar grandes feitos.',
    },
    {
      title: 'Confiança no Senhor',
      verse: '"Confie no Senhor de todo o seu coração e não se apoie em seu próprio entendimento." - Provérbios 3:5',
      message: 'Em tempos de incerteza, nossa confiança deve estar firmada em Deus, não em nossa própria sabedoria. Ele conhece o melhor caminho para nós.',
    },
    {
      title: 'Amor ao Próximo',
      verse: '"Ame o seu próximo como a si mesmo." - Marcos 12:31',
      message: 'O amor é a marca distintiva do cristão. Demonstrar amor ao próximo, em palavra e ação, é viver o evangelho de Cristo no dia a dia.',
    },
  ];

  return (
    <div data-testid="faith-page" className="min-h-screen bg-gray-50">
      {/* Header */}
      <section className="bg-gradient-to-r from-blue-900 to-blue-800 text-white py-20 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-5xl font-bold mb-4">Minha Fé</h1>
            <div className="w-24 h-1 bg-yellow-400 mx-auto mb-6"></div>
            <p className="text-xl text-gray-200 max-w-3xl mx-auto">
              "Mas os que esperam no Senhor renovarão as suas forças, subirão com asas como águias;
              correrão e não se cansarão, andarão e não se fatigarão." - Isaías 40:31
            </p>
          </div>
        </div>
      </section>

      {/* About Faith */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
            {/* Faith Statement */}
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                  <Heart className="text-red-500" size={24} />
                </div>
                <h2 className="text-3xl font-bold text-blue-900">Cristão Evangélico</h2>
              </div>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                Sou cristão evangélico e minha fé em Jesus Cristo é o alicerce da minha vida.
                Busco viver segundo os princípios bíblicos, refletindo o amor de Cristo em todas
                as áreas, incluindo minha vida profissional.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Acredito que a íntegridade, honestidade e compromisso são valores essenciais,
                e procuro sempre honrar a Deus em tudo o que faço.
              </p>
            </div>

            {/* Church */}
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mr-4">
                  <Church className="text-blue-900" size={24} />
                </div>
                <h2 className="text-3xl font-bold text-blue-900">Minha Igreja</h2>
              </div>
              <div data-testid="church-info">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Igreja Batista Atitude
                </h3>
                <p className="text-gray-600 mb-4">Sede Barra da Tijuca - Rio de Janeiro, RJ</p>
                <p className="text-lg text-gray-700 leading-relaxed">
                  Sou membro ativo da Igreja Batista Atitude, onde participo da comunhão cristã,
                  cultos de adoração e estudo da Palavra. É uma bênção fazer parte desta família
                  espiritual que me fortalece e inspira a cada dia.
                </p>
              </div>
            </div>
          </div>

          {/* Devotionals Section */}
          <div className="mb-16">
            <div className="text-center mb-12">
              <div className="flex items-center justify-center mb-4">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                  <Book className="text-blue-900" size={24} />
                </div>
                <h2 className="text-4xl font-bold text-blue-900">Devocionais</h2>
              </div>
              <p className="text-gray-600 text-lg">Reflexões e mensagens para fortalecer sua fé</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {devotionals.map((devotional, index) => (
                <div
                  key={index}
                  data-testid={`devotional-${index}`}
                  className="bg-gradient-to-br from-blue-900 to-blue-800 text-white rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-shadow duration-300"
                >
                  <h3 className="text-2xl font-bold mb-4 text-yellow-400">{devotional.title}</h3>
                  <div className="mb-4 p-4 bg-white/10 rounded-lg">
                    <p className="text-sm italic text-gray-200">{devotional.verse}</p>
                  </div>
                  <p className="text-gray-200 leading-relaxed">{devotional.message}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Values Section */}
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <h2 className="text-3xl font-bold text-blue-900 mb-8 text-center">Valores que Guiam Minha Vida</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { title: 'Integridade', desc: 'Viver com honestidade e transparência' },
                { title: 'Amor', desc: 'Amar a Deus e ao próximo acima de tudo' },
                { title: 'Humildade', desc: 'Reconhecer que tudo vem de Deus' },
                { title: 'Serviço', desc: 'Servir aos outros como Cristo serviu' },
                { title: 'Gratidão', desc: 'Ser grato em todas as circunstâncias' },
                { title: 'Esperança', desc: 'Confiar nas promessas de Deus' },
              ].map((value, index) => (
                <div
                  key={index}
                  data-testid={`value-${index}`}
                  className="p-6 bg-gray-50 rounded-xl border-l-4 border-yellow-400"
                >
                  <h3 className="text-xl font-bold text-blue-900 mb-2">{value.title}</h3>
                  <p className="text-gray-600">{value.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Quote Section */}
      <section className="py-16 bg-gradient-to-r from-blue-900 to-blue-800 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Book className="mx-auto mb-6 text-yellow-400" size={48} />
          <blockquote className="text-2xl md:text-3xl font-serif italic mb-4">
            "Porque Deus amou o mundo de tal maneira que deu o seu Filho unigênito,
            para que todo aquele que nele crê não pereça, mas tenha a vida eterna."
          </blockquote>
          <p className="text-xl text-yellow-400">João 3:16</p>
        </div>
      </section>
    </div>
  );
};

export default Faith;